import type { Translations } from '@/context/LanguageContext';
import type { Event } from '@/types';

/**
 * Render the legacy price/isFree pair as a locale-INDEPENDENT cost string.
 *
 * Twinned with `legacyCostLabel` in
 * cms/server/src/migrations/backfill-event-cost.ts -- change both, or neither.
 * A legacy row must end up with the same stored text whichever path fills it
 * in: the server backfill, or an organizer opening the edit form and saving.
 *
 * Deliberately NOT translated. This value is *stored*, and `cost` is shown
 * verbatim to everyone, so a Spanish-mode editor must not freeze "Gratis" into
 * a field that English readers will also see (Qodo review, finding 2). Free
 * events get both languages, matching the backfill.
 */
export function legacyEventCostLabel(
  event: Pick<Event, 'price' | 'isFree'>,
): string {
  if (event.isFree === true) return 'Free / Gratis';
  if (event.price === null || event.price === undefined) return 'Free / Gratis';
  if (!Number.isFinite(event.price) || event.price <= 0) return 'Free / Gratis';

  const formatted = Number.isInteger(event.price)
    ? String(event.price)
    : event.price.toFixed(2);
  return `$${formatted}`;
}

/**
 * The cost text to display for an event (MWBI-919).
 *
 * `cost` is free text the organizer wrote in their own words, so it is returned
 * verbatim -- no currency symbol is added, no number is rounded, and the
 * wording is never translated. Only surrounding whitespace is stripped.
 *
 * The legacy `price` / `isFree` branch is a fallback, not dead code. Two live
 * sources still produce events without a cost:
 *   1. rows on a deployment where the cost backfill has not run yet, and
 *   2. event objects cached in a returning user's browser -- the event store is
 *      persisted, so a stale snapshot taken before this release has price and
 *      isFree but no cost.
 * Without the fallback those events would render an empty cost. The phase-2
 * follow-up removes the columns and this branch together.
 */
export function getEventCostLabel(
  event: Pick<Event, 'cost' | 'price' | 'isFree'>,
  t: Translations,
): string {
  // Trim BEFORE testing, and test the trimmed length. The trap is whitespace,
  // not emptiness: "   " is a truthy string, so a bare `event.cost || legacy`
  // would treat an all-spaces cost as real and render a blank where the price
  // used to be. (Note "0" is truthy too, so truthiness alone would keep it --
  // the length check is equivalent there, just more explicit about intent.)
  const cost = event.cost?.trim() ?? '';
  if (cost.length > 0) return cost;

  if (event.isFree === true) return t.events.free;
  // price 0 and price null are both "free"; checked explicitly rather than by
  // truthiness so the intent survives the next edit.
  if (event.price === null || event.price === undefined) return t.events.free;
  if (event.price <= 0) return t.events.free;

  return `$${event.price}`;
}
